import { applyClaimAdjustment } from "./claim-adjustment";
import { fraInMonths } from "./fra";
import { resolvePia } from "./pia";
import type { PersonInputs } from "./types";

/**
 * Spousal benefit (simplified).
 *
 * Rules used (https://www.ssa.gov/benefits/retirement/planner/applying7.html):
 *   - Lower earner is entitled to the larger of: their own claim-adjusted benefit OR the spousal benefit.
 *   - Spousal benefit at the lower earner's FRA = 50% of higher earner's PIA.
 *   - Claimed before FRA, spousal benefit is reduced by 25/36 of 1% per month for the first 36 months
 *     early, then by 5/12 of 1% per month for additional months early.
 *   - Spousal benefit does NOT increase with delayed-retirement credits beyond the lower earner's FRA.
 *
 * We compute the spousal portion only (own-benefit comparison happens in the index assembly).
 */
export function spousalBenefit(params: {
  lowerEarner: PersonInputs;
  higherEarnerPia: number;
}): number {
  const { lowerEarner, higherEarnerPia } = params;
  const birthYear = new Date(lowerEarner.dob).getUTCFullYear();
  const fraMonths = fraInMonths(birthYear);
  const claimAgeMonths = Math.round(lowerEarner.claimAge * 12);
  const baseAtFra = 0.5 * higherEarnerPia;

  if (claimAgeMonths >= fraMonths) {
    return baseAtFra;
  }

  const monthsEarly = fraMonths - claimAgeMonths;
  const first36 = Math.min(36, monthsEarly);
  const beyond36 = Math.max(0, monthsEarly - 36);
  const reduction = first36 * (25 / 36) * 0.01 + beyond36 * (5 / 12) * 0.01;
  return baseAtFra * (1 - reduction);
}

/**
 * Identify which spouse is the lower vs higher earner by PIA (resolved from inputs).
 */
export function identifyEarners(primary: PersonInputs, spouse: PersonInputs): {
  higher: { person: PersonInputs; pia: number };
  lower: { person: PersonInputs; pia: number };
} {
  const primaryPia = resolvePia(primary);
  const spousePia = resolvePia(spouse);
  if (primaryPia >= spousePia) {
    return {
      higher: { person: primary, pia: primaryPia },
      lower: { person: spouse, pia: spousePia },
    };
  }
  return {
    higher: { person: spouse, pia: spousePia },
    lower: { person: primary, pia: primaryPia },
  };
}

/**
 * The "effective monthly benefit" the lower earner receives is the larger of:
 *   - their own claim-adjusted benefit
 *   - the spousal benefit
 */
export function lowerEarnerEffectiveBenefit(params: {
  lowerEarner: PersonInputs;
  higherEarnerPia: number;
}): { monthly: number; source: "own" | "spousal" } {
  const { lowerEarner, higherEarnerPia } = params;
  const birthYear = new Date(lowerEarner.dob).getUTCFullYear();
  const lowerPia = resolvePia(lowerEarner);
  const own = applyClaimAdjustment({
    pia: lowerPia,
    birthYear,
    claimAge: lowerEarner.claimAge,
  }).monthlyBenefit;
  const spousal = spousalBenefit({ lowerEarner, higherEarnerPia });
  if (spousal > own) return { monthly: spousal, source: "spousal" };
  return { monthly: own, source: "own" };
}
